import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import SettingsPersonalSecurityProvider from "./SettingsPersonalSecurityProvider";
import ShowToast from "../../../../widget/common/ui/Toast";
import AppTranslation from "../../../../service/app_translation/AppTranslation";
import { AlertMesage } from "../../../../widget/common/ui/Alerts";
import { appRoutes } from "../../../../routes/routes";
import SessionStorage from "../../../../service/storage/SessionStorage";
import StorageKeys from "../../../../const/StorageKeys";
import UtilityHelper from "../../../../service/utilities/UtilityHelper";
import Loader from "../../../../widget/common/ui/Loader";
import { phoneMobileDefault } from "../../../../const/MobileConstant";
import { CountryCodeList } from "../../../../model/country_code/CountryCodeModel";
import { useSelector } from "react-redux";
import { RootState } from "../../../../libraries/state_reducers/store";

type SettingsPersonalSecurityTypes = {
  passwords: SettingsPersonalSecurityFeldsTypes;
  emails: SettingsPersonalSecurityEmailFeldsTypes;
  phone: {
    phone: SettingsPersonalSecurityPhoneFeldsTypes;
    phone_confirm: SettingsPersonalSecurityPhoneFeldsTypes;
  };
  validateStatus: boolean;
  processing: boolean;
  passwordsValidate: PasswordValidateFeldsTypes;
  emailsValidate: EmailValidateFeldsTypes;
  phoneValidate: PhoneValidateFeldsTypes;
  handleOnChange: (
    name: string,
    value: string,
    isValid: boolean | undefined
  ) => void;
  submitPassword: () => void;

  applyCountryCode: (countryCodeDetail: CountryCodeList) => void;
};

type SettingsPersonalSecurityFeldsTypes = {
  password: string;
  password_confirm: string;
};

type PasswordValidateFeldsTypes = {
  password: boolean;
  confirm: boolean;
};

type SettingsPersonalSecurityEmailFeldsTypes = {
  email: string;
  email_confirm: string;
};

type EmailValidateFeldsTypes = {
  email: boolean;
  confirm: boolean;
};

type SettingsPersonalSecurityPhoneFeldsTypes = {
  phone_number: string;
  phone_code: string;
  phone_code_id: string;
};

type PhoneValidateFeldsTypes = {
  phone: boolean;
  phone_confirm: boolean;
};

const SettingsPersonalSecurityContext = createContext<
  SettingsPersonalSecurityTypes | undefined
>(undefined);

export const SettingsPersonalSecurityContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const countryCodeTextField = useSelector(
    (state: RootState) => state.countryCode.textFielName
  );

  const [nameAction, setNameAction] = useState<"password" | "email" | "phone">(
    "password"
  );
  const [processing, setProcessing] = useState<boolean>(false);
  const [validateStatus, setValidateStatus] = useState<boolean>(false);

  const [passwords, setPasswords] =
    useState<SettingsPersonalSecurityFeldsTypes>({
      password: "",
      password_confirm: "",
    });

  const [passwordsValidate, setPasswordsValidate] =
    useState<PasswordValidateFeldsTypes>({ password: false, confirm: false });

  const [emails, setEmails] = useState<SettingsPersonalSecurityEmailFeldsTypes>(
    {
      email: "",
      email_confirm: "",
    }
  );
  const [emailsValidate, setEmailsValidate] = useState<EmailValidateFeldsTypes>(
    { email: false, confirm: false }
  );

  const phoneDefault: SettingsPersonalSecurityPhoneFeldsTypes = {
    phone_number: "",
    phone_code: phoneMobileDefault.phone_code,
    phone_code_id: phoneMobileDefault.phone_code_id,
  };

  const [phone, setPhone] = useState<{
    phone: SettingsPersonalSecurityPhoneFeldsTypes;
    phone_confirm: SettingsPersonalSecurityPhoneFeldsTypes;
  }>({
    phone: phoneDefault,
    phone_confirm: phoneDefault,
  });

  const [phoneValidate, setPhoneValidate] = useState<PhoneValidateFeldsTypes>({
    phone: false,
    phone_confirm: false,
  });

  const applyCountryCode = (countryCodeDetail: CountryCodeList) => {
    if (countryCodeDetail != null) {
      let phoneTemp = { ...phone };

      if (countryCodeTextField === "phone_number_new") {
        phoneTemp = {
          ...phoneTemp,
          phone: {
            ...phoneTemp.phone,
            phone_code: countryCodeDetail.country_phonecode!,
            phone_code_id: countryCodeDetail.country_id!,
          },
        };
      } else if (countryCodeTextField === "phone_number_new_confirm") {
        phoneTemp = {
          ...phoneTemp,
          phone_confirm: {
            ...phoneTemp.phone_confirm,
            phone_code: countryCodeDetail.country_phonecode!,
            phone_code_id: countryCodeDetail.country_id!,
          },
        };
      }
      setPhone(phoneTemp);
    }
  };

  const handleOnChange = (
    fieldName: string,
    fieldValue: string,
    isValid: boolean | undefined
  ) => {
    fieldName = fieldName.toLowerCase();
    let passwordsTemp = { ...passwords },
      passwordsValidateTemp = { ...passwordsValidate },
      emailsTemp = { ...emails },
      emailsValidateTemp = { ...emailsValidate },
      phoneTemp = { ...phone },
      phoneValidateTemp = { ...phoneValidate };

    if (fieldName === `new_password`) {
      passwordsTemp = { ...passwordsTemp, password: fieldValue };
      passwordsValidateTemp = {
        ...passwordsValidateTemp,
        password: isValid ?? true,
      };
    } else if (fieldName === `password_confirm`) {
      passwordsTemp = { ...passwordsTemp, password_confirm: fieldValue };
      passwordsValidateTemp = {
        ...passwordsValidateTemp,
        confirm: isValid ?? true,
      };
    } else if (fieldName === `new_email`) {
      emailsTemp = { ...emailsTemp, email: fieldValue };
      emailsValidateTemp = {
        ...emailsValidateTemp,
        email: isValid ?? true,
      };
    } else if (fieldName === `confirm_new_email`) {
      emailsTemp = { ...emailsTemp, email_confirm: fieldValue };
      emailsValidateTemp = {
        ...emailsValidateTemp,
        confirm: isValid ?? true,
      };
    } else if (fieldName === `phone_number_new`) {
      phoneTemp = {
        ...phoneTemp,
        phone: { ...phoneTemp.phone, phone_number: fieldValue },
      };
      phoneValidateTemp = {
        ...phoneValidateTemp,
        phone: isValid ?? true,
      };
    } else if (fieldName === `phone_number_new_confirm`) {
      phoneTemp = {
        ...phoneTemp,
        phone_confirm: { ...phoneTemp.phone_confirm, phone_number: fieldValue },
      };
      phoneValidateTemp = {
        ...phoneValidateTemp,
        phone_confirm: isValid ?? true,
      };
    }

    setValidateStatus(false);

    if (nameAction === "password") {
      setPasswords(passwordsTemp);
      setPasswordsValidate(passwordsValidateTemp);
    } else if (nameAction === "email") {
      setEmails(emailsTemp);
      setEmailsValidate(emailsValidateTemp);
    } else {
      setPhone(phoneTemp);
      setPhoneValidate(phoneValidateTemp);
    }
  };

  const setupNameAction = () => {
    const pathArr = window.location.pathname.split("/");
    const pathName = pathArr[pathArr.length - 1];

    setNameAction(
      pathName === "update-password"
        ? "password"
        : pathName === "update-email"
        ? "email"
        : "phone"
    );
  };

  const submitPassword = async () => {
    setValidateStatus(true);
    if (nameAction === "password") {
      if (passwordsValidate.password && passwordsValidate.confirm) {
        if (passwords.password === passwords.password_confirm) {
          setProcessing(true);
          const statusResponse =
            await SettingsPersonalSecurityProvider.submitPasswordAPI(
              passwords.password
            );
          setProcessing(false);

          if (statusResponse) {
            alertMessageResponse("password_updated_successfully");
          } else {
            ShowToast.error(AppTranslation.text("something_went_wrong"));
          }
        } else {
          ShowToast.info(AppTranslation.text("password_mismatch"));
        }
      }
    } else if (nameAction === "email") {
      if (emailsValidate.email && emailsValidate.confirm) {
        if (emails.email === emails.email_confirm) {
          const currentEmail: string = SessionStorage.getValue(
            StorageKeys.settings_personal_info_email
          );
          if (emails.email !== currentEmail.toLowerCase()) {
            Loader.modal();
            await SettingsPersonalSecurityProvider.submitEmailAPI(emails.email);
            Loader.modal(false);
          } else {
            alertMessageResponse("email_updated_successfully");
          }
        } else {
          ShowToast.info(AppTranslation.text("email_mismatch"));
        }
      }
    } else if (nameAction === "phone") {
      console.log(phoneValidate, phone);

      if (phoneValidate.phone && phoneValidate.phone_confirm) {
        if (
          `${phone.phone.phone_code_id}${phone.phone.phone_number}` ===
          `${phone.phone_confirm.phone_code_id}${phone.phone_confirm.phone_number}`
        ) {
          const currentPhoneSession: string = SessionStorage.getValue(
            StorageKeys.settings_personal_info_phone_number
          );
          const currentPhoneDetail: {
            phone_code: string;
            phone_code_id: string;
            phone_number: string;
          } = JSON.parse(currentPhoneSession);

          if (
            `${phone.phone.phone_code_id}${phone.phone.phone_number}` !==
            `${currentPhoneDetail.phone_code_id}${currentPhoneDetail.phone_number}`
          ) {
            Loader.modal();
            await SettingsPersonalSecurityProvider.submitPhoneAPI({
              phone_code: phone.phone.phone_code,
              phone_number: phone.phone.phone_number,
              phone_code_id: phone.phone.phone_code_id,
            });
            Loader.modal(false);
          } else {
            alertMessageResponse("phone_updated_successfully");
          }
        } else {
          ShowToast.info(AppTranslation.text("phone_number_mismatch"));
        }
      }
    }
  };

  const alertMessageResponse = (msg: string) => {
    if (UtilityHelper.isNotEmpty(msg)) {
      AlertMesage({
        title: msg,
        onClick: () => {
          SettingsPersonalSecurityProvider.navigate(
            appRoutes.routeNameSettingsPeronalInfo
          );
        },
      });
    }
  };
  useEffect(() => {
    setupNameAction();
  }, []);

  return (
    <SettingsPersonalSecurityContext.Provider
      value={{
        passwords,
        emails,
        phone,
        validateStatus,
        processing,
        passwordsValidate,
        emailsValidate,
        phoneValidate,
        submitPassword,
        handleOnChange,

        applyCountryCode,
      }}
    >
      {children}
    </SettingsPersonalSecurityContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const SettingsPersonalSecurityContextImport = () => {
  const context = useContext(SettingsPersonalSecurityContext);
  if (!context) {
    throw new Error(
      "SettingsPersonalSecurityContextImport must be used within a SettingsPersonalSecurityContextProvider"
    );
  }
  return context;
};
